import API from "../Services/API";
import Model from "./Model";
import { toQueryString } from "../Services/helpers";
import State from "./State";
import { useToasts } from "../Components/Toasts";

const useCustomers = (searchData, alwaysRefresh) => {
    let searchStr = toQueryString(searchData)

    return Model({
        alwaysRefresh,
        stateKey: ['customers', searchStr],
        fetchData: () => API.get('/customers?perPage=9999&include=jobs&'+searchStr),
        returnDataKey: 'customers',
        saveAdditionalData: (response) => {
            response.map((customer) => {
                State.putIfNotExists(['customer', customer.id], customer);
                return customer;
            });
        },
        defaultLoadingError: 'Failed to load customers.',
    });
}

const customerQueue = [];
let customerTimeout = null;
const batchSize = 20;

const fetchCustomersInBatch = async (customerId, showError) => {
    return new Promise((resolve, reject) => {
        customerQueue.push({ customerId, resolve, reject });
        if (customerTimeout) {
            clearTimeout(customerTimeout);
        }
        customerTimeout = setTimeout(() => doFetchCustomerBatch(showError), 50);
    });
}

const doFetchCustomerBatch = (showError) => {
    const queue = customerQueue.splice(0, batchSize);
    const customerIds = queue.map(item => item.customerId);
    API.get('/customers?'+toQueryString({customerIds})).then(data => {
        const keyedById = data.reduce((acc, item) => {
            acc[item.id] = item;
            return acc;
        }, {});
        queue.forEach(item => {
            item.resolve(keyedById[item.customerId] ?? null);
        });
    }).catch(err => showError(API.parseErrorResponse(err, 'There was an error loading the customers.')));
    if (customerQueue.length > 0) {
        customerTimeout = doFetchCustomerBatch(showError);
    }
}

const useCustomer = (customerId, alwaysRefresh, allowBatching = false) => {
    const {showError} = useToasts();

    return Model({
        block: !customerId,
        alwaysRefresh,
        stateKey: ['customer', customerId],
        fetchData: () => {
            if (allowBatching) {
                return fetchCustomersInBatch(customerId, showError);
            }
            return API.get('/customers/'+customerId);
        },
        returnDataKey: 'customer',
        registerAdditionalMethods: (opts) => {
            return {
                updateCustomer: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post('/customers/'+customerId, data),
                        'Customer updated',
                        true,
                    );
                },
                updateCommunicationPreferences: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/communicationPreferences`, data),
                        'Customer preferences updated',
                        true,
                    );
                },
                changeSecurity: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/changeSecurity`),
                        'Customer PIN updated',
                        true,
                    );
                },
                scheduleJob: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/schedule`, data),
                        'Job scheduled',
                        true,
                    );
                },
                markJobAsDone: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/done`, data),
                        'Job marked as done',
                        true,
                    );
                },
                skipJob: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/skip`, data),
                        'Job was skipped',
                        true,
                    );
                },
                updateJob: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs${jobId ? '/'+jobId : ''}`, data),
                        'Job was updated',
                        true,
                    );
                },
                updateJobItemisationAdjustment: (jobId, data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/adjustment`, data),
                        'Job invoice adjustment was updated',
                        true,
                    );
                },
                addExternalPayment: (data) => {
                    data.customerId = customerId;
                    return opts.wrapUpdateCall(
                        () => API.post(`/payments/external`, data),
                        'External payment added',
                        true,
                    );
                },
                addLoyaltyCard: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/loyaltyCards`, data),
                        'Loyalty card added',
                        true,
                    );
                },
                cancelLoyaltyCard: (id) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/loyaltyCards/${id}`),
                        'Loyalty card cancelled',
                        true,
                    );
                },
                editBalance: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/${data.action}`, data),
                        'Balance was updated',
                        true,
                    );
                },
                chargePaymentMethod: (data) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/chargePaymentMethod`, data),
                        'Charge started - it may take a few minutes to show on the customer\'s account',
                        true,
                    );
                },
                cancelJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/cancel`),
                        'Job was cancelled',
                        true,
                    );
                },
                resumeJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/jobs/${jobId}/resume`),
                        'Job was resumed',
                        true,
                    );
                },
                deleteJob: (jobId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/jobs/${jobId}`),
                        'Job was deleted',
                        true,
                    );
                },
                deleteInvoice: (invoiceId) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/invoices/${invoiceId}`),
                        'Invoice was deleted',
                        true,
                    );
                },
                deleteCustomer: () => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}`),
                        'Customer deleted',
                        true,
                    );
                },
                restoreCustomer: () => {
                    return opts.wrapUpdateCall(
                        () => API.post(`/customers/${customerId}/restore`),
                        'Customer restored',
                        true,
                    );
                },
                removeTags: (tagIds) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/tags`, {tagIds}),
                        'Tags removed',
                        true,
                    );
                },
                removeJobTags: (jobId, tagIds) => {
                    return opts.wrapUpdateCall(
                        () => API.delete(`/customers/${customerId}/jobs/${jobId}/tags`, {tagIds}),
                        'Tags removed',
                        true,
                    );
                }
            }
        },
        defaultLoadingError: 'Failed to load customer.',
    });
}

const useReport = (alwaysRefresh = false) => {
    return Model({
        stateKey: 'customer-report',
        alwaysRefresh: alwaysRefresh,
        fetchData: () => API.get(`/customers/report`),
        returnDataKey: 'report',
    })
}

export {
    useCustomers,
    useCustomer,
    useReport,
};
